import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toaster } from "baseui/toast";
import { createUserEstate, getUserEstate, updateUserEstateRole, type UpdateUserEstateRolePayload, type CreateUserEstatePayload, type GetUserEstateParams, type UserEstate } from "../api/userEstate";

// Extended params interface with only valid user_estate fields
interface UseUserEstateParams {
  // Only real user_estate fields that can be filtered directly
  user_id?: number | string; // Single user ID
  user_id_in?: string; // Comma-separated user IDs
  estate_id_in?: string; // Comma-separated estate IDs
  status_id?: number | string; // Single status ID
  status_id_in?: string; // Comma-separated status IDs
  user_type_id_in?: string; // Comma-separated user type IDs

  // Native backend search params (search by name/text directly)
  user_search?: string; // Search within user fields (name, last name, full name, email, phone)
  estate_search?: string; // Search within estate name

  // Date range filters
  created_at_gte?: string; // Date greater than or equal
  created_at_lte?: string; // Date less than or equal

  // Pagination
  page?: number;
  limit?: number;

  // Sorting
  order_by?: string;
  order_direction?: 'asc' | 'desc';

  enabled?: boolean; // Control whether the query should run
}

// Custom hook for User Estate requests with pagination and filtering
export const useUserEstate = (params?: UseUserEstateParams) => {
  // Separate enabled from API parameters
  const { enabled = true, ...apiParams } = params || {};
  
  // Build query parameters with only valid user_estate fields
  const queryParams: any = {
    page: apiParams?.page || 1,
    limit: apiParams?.limit || 20,
    expand: 'estate,estate_id,user_type,user_type_id,user_estate_status,status_id,user,user_id',
  };

  // Add valid user_estate field filters using _in suffix
  if (apiParams?.user_id_in && apiParams.user_id_in.trim()) {
    queryParams.user_id_in = apiParams.user_id_in;
  }
  
  // Add individual user_id filter 
  if (apiParams?.user_id) {
    queryParams.user_id = apiParams.user_id;
  }
  
  if (apiParams?.estate_id_in && apiParams.estate_id_in.trim()) {
    queryParams.estate_id_in = apiParams.estate_id_in;
  }
  
  // Add individual status_id filter
  if (apiParams?.status_id && String(apiParams.status_id).trim()) {
    queryParams.status_id = apiParams.status_id as any;
  }
  
  if (apiParams?.status_id_in && apiParams.status_id_in.trim()) {
    queryParams.status_id_in = apiParams.status_id_in;
  }

  if (apiParams?.user_type_id_in && apiParams.user_type_id_in.trim()) {
    queryParams.user_type_id_in = apiParams.user_type_id_in;
  }

  // Native search params — passed directly to the backend
  if (apiParams?.user_search && apiParams.user_search.trim()) {
    queryParams.user_search = apiParams.user_search.trim();
  }

  if (apiParams?.estate_search && apiParams.estate_search.trim()) {
    queryParams.estate_search = apiParams.estate_search.trim();
  }

  // Convert date range to proper format if provided
  if (apiParams?.created_at_gte) {
    queryParams.created_at_gte = apiParams.created_at_gte;
  }
  
  if (apiParams?.created_at_lte) {
    queryParams.created_at_lte = apiParams.created_at_lte;
  }

  // Add other valid parameters
  if (apiParams?.order_by) {
    queryParams.order_by = apiParams.order_by;
  }
  
  if (apiParams?.order_direction) {
    queryParams.order_direction = apiParams.order_direction;
  }

  const { data: userEstateResponse, isLoading, error, isError, refetch } = useQuery({
    queryKey: ['/user_estate', queryParams],
    queryFn: ({ signal }) => getUserEstate(queryParams, signal),
    retry: 1,
    enabled, // Use the separated enabled parameter
  });
  
  // Extract data from response 
  // Axios response structure: userEstateResponse.data contains the API response
  // API response structure: { "data": [...], "total_pages": 2, "page": 1, "total_elements": 22 }
  const apiResponse = userEstateResponse;
  const userEstates: UserEstate[] = Array.isArray(apiResponse?.data) ? apiResponse.data : [];
  
  // Extract pagination metadata from API response (same level as data array)
  const totalPages = (apiResponse as any)?.total_pages || 1;
  const currentPage = (apiResponse as any)?.page || (apiParams?.page || 1);
  const totalElements = (apiResponse as any)?.total_elements || 0;

  return {
    userEstates,
    isLoading,
    error,
    // SAM-801: the failure and a retry, so a list can tell "could not load" from "no rows".
    isError,
    refetch,
    // Pagination data
    totalPages,
    currentPage,
    totalElements,
  };
};

/**
 * Mutation hook to add a user_estate. Invalidates the cached `/user_estate`
 * lists on success so consumers (UserDetail estate tab, PropertyAdmin) refresh
 * automatically.
 */
export const useCreateUserEstate = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateUserEstatePayload) => createUserEstate(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/user_estate'] });
      toaster.positive('Estate added to user', { autoHideDuration: 3000 });
    },
    onError: (error: any) => {
      toaster.negative(error?.response?.data?.message || error?.message || 'Failed to add estate', { autoHideDuration: 3000 });
    },
  });
};

/** SAM-973 — change the role of a unit already on the user. Refreshes the `/user_estate` lists. */
export const useUpdateUserEstateRole = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: UpdateUserEstateRolePayload) => updateUserEstateRole(payload),
    onSuccess: () => {
      toaster.positive('Role updated', { autoHideDuration: 3000 });
    },
    onError: (error: any) => {
      toaster.negative(error?.response?.data?.message || 'Failed to update role', { autoHideDuration: 4000 });
    },
    // Returned, so the mutation stays pending until the list is re-read: the select shows the new role
    // all the way through instead of falling back to the old one for a moment.
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['/user_estate'], exact: false }),
  });
};
